import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';

// Centralized product data
const products = [
    {
        id: 1,
        image: require('../images/products/insulation-pinkbatts.jpg'),
        name: "Pink Batts Insulation",
        type: "insulation",
        brand: "Pink Batts",
        description: "Popular thermal and acoustic insulation made from lightweight glass wool, known for its distinctive pink color. Designed to improve energy efficiency and comfort in walls, ceilings, and underfloors."
    },
    {
        id: 2,
        image: require('../images/products/insulation-eliment.png'),
        name: "Eliment Insulation",
        type: "insulation",
        brand: "Eliment",
        description: "High-performance thermal solution designed to enhance energy efficiency in residential and commercial buildings. Offers excellent heat retention and moisture resistance, ideal for New Zealand's diverse climate conditions."
    },
    {
        id: 3,
        image: require('../images/products/insulation-knauf.jpg'),
        name: "Knauf Insulation",
        type: "insulation",
        brand: "Knauf",
        description: "Durable and eco-friendly solution made from glass mineral wool, designed to deliver excellent thermal and acoustic performance. Known for its sustainability and ease of installation."
    },
    {
        id: 4,
        image: require('../images/products/insulation-mammoth.png'),
        name: "Mammoth Insulation",
        type: "insulation",
        brand: "Mammoth",
        description: "New Zealand-made, polyester-based insulation known for its durability, safety, and sustainable manufacturing. Soft, non-irritating, and free from harsh chemicals, ideal for creating warmer, quieter, and healthier indoor environments."
    },
    {
        id: 5,
        image: require('../images/products/insulation-greenstuf.png'),
        name: "GreenStuf Insulation",
        type: "insulation",
        brand: "GreenStuf",
        description: "New Zealand-made, 100% polyester insulation designed to provide long-lasting thermal and acoustic performance. Non-toxic, non-irritating, and environmentally friendly, making it a safe choice for homes, schools, and commercial buildings."
    },
    {
        id: 6,
        image: require('../images/products/insulation-expol.png'),
        name: "Expol Insulation",
        type: "insulation",
        brand: "Expol",
        description: "Rigid polystyrene-based solution commonly used for underfloor and wall insulation in both new builds and retrofits. Lightweight, moisture-resistant, and durable, helps improve energy efficiency and comfort in a wide range of building types."
    },
    {
        id: 7,
        image: require('../images/products/garage-2.jpg'),
        name: "Smooth Panel Garage Door",
        type: "garage door",
        brand: "Door",
        description: "Modern smooth panel garage door with sleek, contemporary design. Features smooth operation and durable construction, perfect for residential properties seeking a clean, minimalist aesthetic."
    },
    {
        id: 8,
        image: require('../images/products/garage-1.jpg'),
        name: "Flat Embossed Panel Garage Door",
        type: "garage door",
        brand: "Door",
        description: "Classic flat embossed panel garage door with textured design. Combines traditional elegance with modern functionality, ideal for homes that value both style and durability."
    }
];

// Product Card Component
const ProductCard = ({ product }) => {
    return (
        <motion.div
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full flex flex-col"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -5 }}
        >
            <div className="h-64 overflow-hidden flex-shrink-0">
                <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                    }}
                />
                <div
                    className="w-full h-full bg-gray-200 flex items-center justify-center"
                    style={{ display: "none" }}
                >
                    <p className="text-gray-500 text-center">[Product Image]</p>
                </div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-brand-orange bg-brand-orange/10 px-3 py-1 rounded-full">
                        {product.type}
                    </span>
                    <span className="text-sm text-gray-600 font-medium">
                        {product.brand}
                    </span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{product.name}</h3>
                <p className="text-gray-600 leading-relaxed flex-1 text-sm">{product.description}</p>
            </div>
        </motion.div>
    );
};

function Products() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Force scroll to top and ensure it's applied
        window.scrollTo(0, 0);

        // Add a small delay to ensure the scroll is applied
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [showTypeFilter, setShowTypeFilter] = useState(true);
    const [showBrandFilter, setShowBrandFilter] = useState(true);

    // Get unique brands from products
    const brands = useMemo(() => {
        const uniqueBrands = [...new Set(products.map(product => product.brand))];
        return uniqueBrands.sort();
    }, []);

    // Get unique types from products
    const types = useMemo(() => {
        const uniqueTypes = [...new Set(products.map(product => product.type))];
        return uniqueTypes.sort();
    }, []);

    // Filter products based on selected filters
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(product.type);
            const brandMatch = selectedBrands.length === 0 || selectedBrands.includes(product.brand);
            return typeMatch && brandMatch;
        });
    }, [selectedTypes, selectedBrands]);

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-cream" style={{ opacity: 1 }}>
            {/* Hero Section */}
            <section className="bg-brand-green text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h1
                        className="text-4xl md:text-6xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Our Products
                    </motion.h1>
                    <motion.p
                        className="text-xl md:text-2xl max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        Discover our premium selection of property maintenance and improvement products.
                    </motion.p>
                </div>
            </section>

            {/* Filter and Products Section */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Filter Sidebar */}
                        <motion.div
                            className="lg:w-64 flex-shrink-0"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <div className="sticky top-8">
                                {/* Product Type Filter */}
                                <div className="mb-8">
                                    <button
                                        onClick={() => setShowTypeFilter(!showTypeFilter)}
                                        className="flex items-center justify-between w-full text-left mb-4"
                                    >
                                        <span className="text-lg font-semibold text-black">Product Type</span>
                                        <svg
                                            className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${showTypeFilter ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {showTypeFilter && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="space-y-3"
                                        >
                                            {types.map(type => (
                                                <label key={type} className="flex items-center space-x-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTypes.includes(type)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedTypes([...selectedTypes, type]);
                                                            } else {
                                                                setSelectedTypes(selectedTypes.filter(t => t !== type));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-brand-orange border-gray-300 rounded focus:ring-brand-orange"
                                                    />
                                                    <span className="text-gray-700 capitalize">{type}</span>
                                                </label>
                                            ))}
                                        </motion.div>
                                    )}
                                </div>

                                {/* Product Brand Filter */}
                                <div className="mb-8">
                                    <button
                                        onClick={() => setShowBrandFilter(!showBrandFilter)}
                                        className="flex items-center justify-between w-full text-left mb-4"
                                    >
                                        <span className="text-lg font-semibold text-black">Product Brand</span>
                                        <svg
                                            className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${showBrandFilter ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {showBrandFilter && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="space-y-3"
                                        >
                                            {brands.map(brand => (
                                                <label key={brand} className="flex items-center space-x-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBrands.includes(brand)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedBrands([...selectedBrands, brand]);
                                                            } else {
                                                                setSelectedBrands(selectedBrands.filter(b => b !== brand));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-brand-orange border-gray-300 rounded focus:ring-brand-orange"
                                                    />
                                                    <span className="text-gray-700">{brand}</span>
                                                </label>
                                            ))}
                                        </motion.div>
                                    )}
                                </div>

                                {/* Results Count */}
                                <div className="pt-6 border-t border-gray-200">
                                    <p className="text-gray-600">
                                        Showing {filteredProducts.length} of {products.length} products
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Products Grid */}
                        <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                                {filteredProducts.map((product, index) => (
                                    <motion.div
                                        key={product.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                    >
                                        <ProductCard product={product} />
                                    </motion.div>
                                ))}
                            </div>

                            {/* No Results Message */}
                            {filteredProducts.length === 0 && (
                                <motion.div
                                    className="text-center py-16"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <h3 className="text-2xl font-bold text-gray-600 mb-4">No products found</h3>
                                    <p className="text-gray-500 mb-6">
                                        Try adjusting your filters to see more products.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSelectedTypes([]);
                                            setSelectedBrands([]);
                                        }}
                                        className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange-hover transition-colors"
                                    >
                                        Clear All Filters
                                    </button>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Products;
